// helpers.ts — Pure utility functions for D203 form

/** Convert ISO date string to HTML date input value (YYYY-MM-DD) */
export const dateToInput = (v: string | null | undefined): string => {
  if (!v) return '';
  return v.substring(0, 10);
};

/** Convert HTML date input value to ISO date string */
export const inputToDate = (v: string): string | null => {
  if (!v) return null;
  return v + 'T00:00:00Z';
};

/** Format number with locale grouping, max 2 decimals */
export const fmt = (v: number | null | undefined): string => {
  if (v == null || v === 0) return '0';
  return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

/** Derive the status badge for a record */
export const getStatusBadge = (record: {
  fd130_80?: string;
  fd130_26?: string;
} | null): { text: string; cls: string } | null => {
  if (!record) return null;
  const s80 = (record.fd130_80 || '').trim();
  const s26 = (record.fd130_26 || '').trim();
  // FD130_80 統一碼：N=草稿/退回/沖銷、D=送審、P=核准（舊碼 S/R 過渡容忍，批次 5）
  if (s26 === 'P') return { text: '已結案', cls: 'badge-default' };
  if (s80 === 'P') return { text: '已核准', cls: 'badge-success' };
  if (s80 === 'D' || s80 === 'S') return { text: '送審中', cls: 'badge-warning' };
  if (s80 === 'R') return { text: '已退回', cls: 'badge-danger' }; // 舊碼殘留顯示容忍（退回現為 N）
  return { text: '未送審', cls: 'badge-default' };
};

/** Check if record is locked for editing */
export const isLockedForEdit = (record: {
  fd130_80?: string;
  fd130_26?: string;
} | null): boolean => {
  if (!record) return false;
  const s80 = (record.fd130_80 || '').trim();
  const s26 = (record.fd130_26 || '').trim();
  return s80 === 'P' || s80 === 'D' || s80 === 'S' || s26 === 'P';
};

const ALWAYS_READONLY = new Set([
  'fd130_01', 'fd130_33', 'fd130_42', 'fd130_43', 'fd130_44', 'fd130_34',
]);

/** Determine if a field should be disabled */
export const isFieldDisabledFn = (key: string, isEditing: boolean, _mode: string): boolean => {
  if (!isEditing) return true;
  if (ALWAYS_READONLY.has(key)) return true;
  return false;
};

/** Approval status label map */
export const APPROVAL_STATUS: Record<string, string> = {
  N: '未送審',
  D: '送審中', // 統一送審碼（批次 5）
  S: '送審中', // 舊碼過渡容忍
  P: '已核准',
  R: '已退回', // 舊碼殘留顯示容忍（退回現為 N）
  '2': '部長已審核',
  '3': '處長已審核',
  '4': '課長已審核',
  '5': '組長已審核',
};

/** Processing category labels */
export const PROCESSING_CATEGORY: Record<number, string> = {
  1: '織造',
  2: '染整',
  3: '防水透濕',
};

/** Pricing method labels */
export const PRICING_METHOD: Record<number, string> = {
  1: '計量',
  2: '計缸',
};

/** Material category labels */
export const MATERIAL_CATEGORY: Record<number, string> = {
  1: '原料',
  2: '半成品',
  3: '成品',
};

/** Color category labels (fd131_22) — 色卡類別，FoxPro 真相 {1,2}（pwd203f1_procedures.md:2317
 *  DO CASE），A9 裁決 human_approved、後端 validator 硬擋 {1,2}。舊值「素色/印花/色織」為
 *  讀後感時代腦補語義（前端鏡射機 2026-07-13 首咬），勿回填。 */
export const COLOR_CATEGORY: Record<string, string> = {
  '1': 'SWATCH',
  '2': 'PANTON CARD',
};
